import type { Data, DataKey, Database, Item, ItemQuery, QueryStore } from "shelving";
import { ASIDE } from "../base/Aside.js";
import { wrapText } from "../util/react.js";
import { PaginateButton } from "./PaginateButton.js";

/** Props used for all lists of things. */
export interface ListProps {
	/** Additional modifiers for the default wrapper element. */
	mods?: string | undefined;
	/** Additional element to show before the list items. */
	before?: React.ReactNode | undefined;
	/** Additional element to show after the list items. */
	after?: React.ReactNode | undefined;
	/** Shown if the list was empty. */
	empty?: React.ReactNode;
	/** Message that's shown to load more. */
	more?: string | undefined;
	/** Detect scroll and automatically load more when the loading element is fully scrolled (defaults to `false`). */
	detect?: boolean | undefined;
}

/** Props used for lists of items sourced from a `Query` around a database collection. */
export interface QueryProps<T extends Data> extends ListProps {
	/** Query of items. */
	query: ItemQuery<T>;
}

/** Props used for lists of items sourced from a `Query` around a database collection. */
export interface OptionalQueryProps<T extends Data> extends ListProps {
	/** Optional query of items. */
	query?: ItemQuery<T> | undefined;
}

/** Props used for lists of items stored in a `QueryStore` from a database collection. */
export interface QueryStoreProps<T extends Database, K extends DataKey<T>> extends ListProps {
	/** Store containing items. */
	store: QueryStore<T, K>;
	/** Map an item to an element. */
	mapper: (item: Item<T[K]>, index: number) => React.ReactElement | null;
}

/** Output a queried set of results as a list with pagination. */
export function List<T extends Database, K extends DataKey<T>>({
	mods = "", //
	after = null,
	before = null,
	store,
	empty = `No ${store.collection}s`,
	more,
	detect,
	mapper,
}: QueryStoreProps<T, K>): React.ReactElement | null {
	const { value, hasMore } = store;
	return value.length ? (
		<section className={mods}>
			{before}
			{value.map(mapper)}
			{after}
			{more !== null && value.length && hasMore ? (
				<PaginateButton store={store} detect={detect} mods="size-small button-quiet">
					{more}
				</PaginateButton>
			) : null}
		</section>
	) : (
		wrapText(empty, ASIDE)
	);
}
